import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { DeleteIpdEntry, GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";

const ListIpdEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchOpds = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetIpdEntries(); // Your API should return a list of OPD entries
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          ipdNo: item.ipdNo || "",
          date: item.date ? new Date(item.date).toLocaleDateString() : "",
          patientName:
            (item.patientId?.patientNamePrefix ? item.patientId?.patientNamePrefix + " " : "") +
            (item.patientId?.patientName || ""),
          sex: item.patientId?.sex || "",
          age: item.patientId?.age ? `${item.patientId?.age} ${item.patientId?.ageSuffix || ""}` : "",
          mobileNo: item.patientId?.mobileNo || "",
          address: item.patientId?.address || "",
          consultantDoctor: item.consultantDoctorId?.name || "",
          department: item.departmentId?.name || "",
          foreiger: item.foreiger || "No",
          advance: item.advance || 0,
          paymentMode: item.paymentMode || 0,
        }));
        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchOpds();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => {
    navigate(`/ipd/transaction/ipd-entry/edit/${id}`);
  };

  const handleViewClick = id => {
    navigate(`/ipd/transaction/ipd-entry/view/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteIpdEntry(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, sortable: true, width: "8%" },
    { name: "IPD No", selector: row => row.ipdNo, sortable: true, width: "10%" },
    { name: "Date", selector: row => row.date, sortable: true, width: "10%" },
    { name: "Patient Name", selector: row => row.patientName, sortable: true, width: "15%" },
    { name: "Sex", selector: row => row.sex, width: "10%" },
    { name: "Age", selector: row => row.age, width: "10%" },
    { name: "Mobile No", selector: row => row.mobileNo, width: "10%" },
    { name: "Doctor", selector: row => row.consultantDoctor, width: "15%" },
    { name: "Department", selector: row => row.department, width: "10%" },
    { name: "Foreiger", selector: row => row.foreiger, width: "10%" },
    { name: "Advance", selector: row => row.advance, sortable: true, width: "15%" },
    { name: "Payment Mode", selector: row => row.paymentMode, sortable: true, width: "15%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-info me-2"
            onClick={() => handleViewClick(row.id)}
            id={`view-${row.id}`}
          >
            <i className="fa fa-eye" />
          </button>
          <UncontrolledTooltip placement="top" target={`view-${row.id}`}>
            View
          </UncontrolledTooltip>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "25%"
    }
  ];

  const headers = [
    { label: "S.No", key: "index" },
    { label: "IPD No", key: "ipdNo" },
    { label: "Date", key: "date" },
    { label: "Patient Name", key: "patientName" },
    { label: "Sex", key: "sex" },
    { label: "Age", key: "age" },
    { label: "Mobile No", key: "mobileNo" },
    { label: "Address", key: "address" },
    { label: "Doctor", key: "consultantDoctor" },
    { label: "Department", key: "department" },
    { label: "Foreigner", key: "foreiger" },
    { label: "Advance", key: "advance" },
    { label: "Payment Mode", key: "paymentMode" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="ipd_entries.csv">
      <button className="btn btn-primary mt-1" id="export">
        Export
      </button>
      <UncontrolledTooltip placement="top" target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>
  );

  return (
    <Fragment>
      <Breadcrumb parent="IPD" title="List IPD Entries" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5>List IPD Entries</h5>
                  <Link to={`/ipd/transaction/ipd-entry/add`}>
                    <button className="btn btn-primary" id="create">
                      Create IPD Entry
                    </button>
                    <UncontrolledTooltip placement="top" target="create">
                      Create New IPD Entry
                    </UncontrolledTooltip>
                  </Link>
                </div>

                <div className="row mt-2">
                  <div className="col-sm-6">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search by patient, doctor, or IPD No..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="col-sm-6 text-end">
                    <ExportCSV data={data} />
                  </div>
                </div>
              </div>

              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  highlightOnHover
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListIpdEntry;
